import React, { useState, useEffect, useRef, useCallback } from "react";
import "./styles.css";
import { supabase } from "./supabaseClient";
import { calculateCustomHandicap } from "./views/HandicapService";
import { Stats } from "./views/Stats.js";
import Navigation from "./components/Navigation";
import Login from "./views/Login";
import Dashboard from "./views/Dashboard";
import Setup from "./views/Setup";
import Scorecard from "./views/Scorecard";
import RoundHistory from "./views/RoundHistory";
import RoundSummary from "./views/RoundSummary";
import Leaderboard from "./views/Leaderboard.js";
import CourseManager, { CourseCreator } from "./views/CourseManager";
import PlayerManager from "./views/PlayerManager";
import UpdatePassword from "./views/UpdatePassword";
import VerticalScorecard from "./views/VerticalScorecard";
import { BirdieBet } from "./views/BirdieBet.js";
import MajorsView from "./views/MajorsView";
import POTYView from "./views/POTYView";
import { calculatePOTY } from "./POTYService";
import PlayersList from "./views/PlayersList";
import Signup from "./views/Signup";
import ForgotPassword from "./views/ForgotPassword";
import HoleStats from "./views/HoleStats";
import HoleStatsBreakdown from "./views/HoleStatsBreakdown";
import DiceRoller from "./views/DiceRoller";
import BirdieBetView from "./views/BirdieBetView";
import QuickRound from "./views/QuickRound";
// --- PURE HELPERS ---
const getGrossScore = (scores, player, holeNum) =>
  scores[player]?.[holeNum] || 0;

const getHoleData = (course, holeNum) => course?.holes[holeNum - 1];

const getNetScore = (grossScore, playerHandicap, holeDifficulty) => {
  if (!grossScore) return 0;
  const baseStrokes = Math.floor(playerHandicap / 18);
  const remainder = playerHandicap % 18;
  const extraStroke = Number(holeDifficulty) <= remainder ? 1 : 0;
  return grossScore - (baseStrokes + extraStroke);
};

const calculateNextQuota = (currentQuota, pointsEarned) => {
  const diff = pointsEarned - currentQuota;
  if (diff < 0) return Math.max(0, currentQuota - 1);
  if (diff > 0) return Math.ceil(currentQuota + diff * 0.5);
  return currentQuota;
};

// --- COMPONENTS ---
const BottomNav = ({ currentView, setView, isRoundActive }) => {
  if (
    ["scorecard", "login", "updatePassword", "signup", "holeStats"].includes(
      currentView
    )
  )
    return null;
  const navItemStyle = (isActive) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "none",
    width: "33%",
    cursor: "pointer",
    color: isActive ? "#1b4332" : "#aaa",
    transition: "all 0.2s ease",
  });
  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: "60px",
        backgroundColor: "white",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        borderTop: "1px solid #eee",
        zIndex: 1000,
        paddingBottom: "10px",
        boxShadow: "0 -4px 15px rgba(0,0,0,0.05)",
      }}
    >
      <style>{`
        @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.3; } 100% { opacity: 1; } }
        .live-dot { width: 6px; height: 6px; background-color: #ff4d4d; border-radius: 50%; 
                    display: inline-block; margin-right: 4px; animation: pulse 1.5s infinite; }
      `}</style>

      {/* HOME TAB */}
      <button
        style={navItemStyle(currentView === "dashboard")}
        onClick={() => setView("dashboard")}
      >
        <span style={{ fontSize: "1.2rem" }}>🏠</span>
        <span style={{ fontSize: "0.6rem", fontWeight: "700" }}>HOME</span>
      </button>

      <button
        style={navItemStyle(currentView === "playersList")}
        onClick={() => setView("playersList")}
      >
        <span style={{ fontSize: "1.2rem" }}>👥</span>
        <span style={{ fontSize: "0.6rem", fontWeight: "700" }}>PLAYERS</span>
      </button>

      {/* LEADERBOARD TAB */}
      <button
        style={navItemStyle(currentView === "leaderboard")}
        onClick={() => setView("leaderboard")}
      >
        <span style={{ fontSize: "1.2rem" }}>
          {isRoundActive ? "📊" : "🏆"}
        </span>
        <span
          style={{
            fontSize: "0.6rem",
            fontWeight: "800",
            color: isRoundActive ? "#ff4d4d" : "inherit",
            display: "flex",
            alignItems: "center",
          }}
        >
          {isRoundActive && <span className="live-dot"></span>}
          {isRoundActive ? "LIVE" : "LEADERBOARD"}
        </span>
      </button>

      {/* PLAY / RESUME TAB */}
      <button
        style={navItemStyle(
          currentView === "setup" || currentView === "verticalScorecard"
        )}
        onClick={() => setView(isRoundActive ? "scorecard" : "setup")}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.3rem",
            height: "38px",
            width: "38px",
          }}
        >
          ⛳
        </div>
        <span
          style={{
            fontSize: "0.6rem",
            fontWeight: "700",
            color: "#1b4332",
            marginTop: "2px",
          }}
        >
          {isRoundActive ? "RESUME" : "PLAY"}
        </span>
      </button>
    </div>
  );
};

export default function App() {
  // --- 1. STATE ---
  // Add these to the top of your App component state section
  const [advancedStats, setAdvancedStats] = useState({});
  const [targetPlayer, setTargetPlayer] = useState(null);
  const [majors, setMajors] = useState([]);
  const [appReady, setAppReady] = useState(false);
  const [view, setView] = useState(
    () => localStorage.getItem("activeView") || "login"
  );
  const [user, setUser] = useState(null);
  const [statsTargetPlayer, setStatsTargetPlayer] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [breakdownHoleStats, setBreakdownHoleStats] = useState([]);
  const [keptHoles, setKeptHoles] = useState(() =>
    JSON.parse(localStorage.getItem("keptHoles") || "{}")
  );
  const [selectedCourse, setSelectedCourse] = useState(() =>
    JSON.parse(localStorage.getItem("selectedCourse") || "null")
  );
  const [players, setPlayers] = useState(() =>
    JSON.parse(localStorage.getItem("players") || "[]")
  );
  const [scores, setScores] = useState(() =>
    JSON.parse(localStorage.getItem("scores") || "{}")
  );
  const [matchConfig, setMatchConfig] = useState(() =>
    JSON.parse(
      localStorage.getItem("matchConfig") ||
        '{"mode":"1v1","teamA":[],"teamB":[]}'
    )
  );
  const [vegasConfig, setVegasConfig] = useState(() =>
    JSON.parse(localStorage.getItem("vegasConfig") || '{"teamA":[],"teamB":[]}')
  );
  const [matchTeams, setMatchTeams] = useState(() =>
    JSON.parse(localStorage.getItem("matchTeams") || "null")
  );
  const [currentHoleIndex, setCurrentHoleIndex] = useState(
    () => Number(localStorage.getItem("currentHoleIndex")) || 0
  );
  const [playerHandicaps, setPlayerHandicaps] = useState(() =>
    JSON.parse(localStorage.getItem("playerHandicaps") || "{}")
  );
  const [roundDate, setRoundDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [spectatingRound, setSpectatingRound] = useState(null);
  const [startingHole, setStartingHole] = useState(1);
  const [selectedGames, setSelectedGames] = useState(() =>
    JSON.parse(localStorage.getItem("selectedGames") || '["Stroke Play"]')
  );
  const [isNassau, setIsNassau] = useState(() =>
    JSON.parse(localStorage.getItem("isNassau") || "false")
  );
  const [courseDirectory, setCourseDirectory] = useState([]);
  const [playerDirectory, setPlayerDirectory] = useState([]);
  const [roundHistory, setRoundHistory] = useState([]);
  const [editingCourse, setEditingCourse] = useState(null);
  const [activeMajorInfo, setActiveMajorInfo] = useState({
    isMajor: false,
    majorName: null,
  });
  const [selectedSummaryRound, setSelectedSummaryRound] = useState(null);
  const [statsReturnView, setStatsReturnView] = useState("leaderboard");
  const isLoaded = useRef(false);
  const isAdmin = user?.email?.toLowerCase() === "matt.ostrom@gmail.com";
  const isRoundActive = players.length > 0;
  const [holeStatsSource, setHoleStatsSource] = useState("stats");
  const [scorecardStatsPlayer, setScorecardStatsPlayer] = useState(null);
  // Replace the old line 175 with this:
  const [diceCount, setDiceCount] = useState(1);
  const [showDeepLinkBanner, setShowDeepLinkBanner] = useState(false);
  const [isPublicView, setIsPublicView] = useState(false);
  const [isLiveShareView, setIsLiveShareView] = useState(false);
  const [liveShareId, setLiveShareId] = useState(null);
  const [vegasPointValue, setVegasPointValue] = useState(() => parseFloat(localStorage.getItem("vegasPointValue")) || 0);
  const [showVegasValueModal, setShowVegasValueModal] = useState(false);
  const [vegasValuePrompted, setVegasValuePrompted] = useState(false);
  const [vegasPresses, setVegasPresses] = useState(() =>
    JSON.parse(localStorage.getItem("vegasPresses") || '{"front":{"teamA":null,"teamB":null},"back":{"teamA":null,"teamB":null}}')
  );
  const [useGroups, setUseGroups] = useState(false);
  const [groups, setGroups] = useState([]);
  const [activeRound, setActiveRound] = useState(null);
  const [shareId, setShareId] = useState(null);
  const matchConfigRef = useRef(matchConfig);
  const vegasConfigRef = useRef(vegasConfig);
  const selectedGamesRef = useRef(selectedGames);
  const handicapOverridesRef = useRef({});
  const intentionalLogoutRef = useRef(false);
  useEffect(() => {
    matchConfigRef.current = matchConfig;
  }, [matchConfig]);
  useEffect(() => {
    vegasConfigRef.current = vegasConfig;
  }, [vegasConfig]);
  useEffect(() => {
    selectedGamesRef.current = selectedGames;
  }, [selectedGames]);
  const rawStandings = calculatePOTY(roundHistory, majors, playerDirectory);
  const potyStandings = rawStandings.map((standing) => {
    // Find this player in your directory to see if they are a member
    const playerProfile = playerDirectory.find(
      (p) => p.full_name === standing.name
    );
    return {
      ...standing,
      is_member: playerProfile?.is_member, // Attach the missing true/false flag
    };
  });

  // --- 2. LOCAL STORAGE SYNC ---
  useEffect(() => {
    if (isLoaded.current) {
      localStorage.setItem("activeView", view);
      // Guard: don't overwrite round data with empty state while actively scoring.
      // Only cancelRound and handleFinishRound should clear these.
      const isActivelyScorecarding = view === "scorecard";
      if (!isActivelyScorecarding || selectedCourse !== null) {
        localStorage.setItem("selectedCourse", JSON.stringify(selectedCourse));
      }
      if (!isActivelyScorecarding || players.length > 0) {
        localStorage.setItem("players", JSON.stringify(players));
      }
      if (!isActivelyScorecarding || Object.keys(scores).length > 0) {
        localStorage.setItem("scores", JSON.stringify(scores));
      }
      localStorage.setItem("playerHandicaps", JSON.stringify(playerHandicaps));
      localStorage.setItem("currentHoleIndex", currentHoleIndex);
      localStorage.setItem("selectedGames", JSON.stringify(selectedGames));
      localStorage.setItem("matchTeams", JSON.stringify(matchTeams));
      localStorage.setItem("isNassau", JSON.stringify(isNassau));
      localStorage.setItem("keptHoles", JSON.stringify(keptHoles));
      localStorage.setItem("matchConfig", JSON.stringify(matchConfig));
      localStorage.setItem("vegasPresses", JSON.stringify(vegasPresses));
      localStorage.setItem("useGroups", JSON.stringify(useGroups));
      localStorage.setItem("groups", JSON.stringify(groups));
      localStorage.setItem("vegasConfig", JSON.stringify(vegasConfig));
    } else {
      isLoaded.current = true;
    }
  }, [
    view,
    selectedCourse,
    players,
    scores,
    playerHandicaps,
    currentHoleIndex,
    selectedGames,
    matchTeams,
    isNassau,
    keptHoles,
    matchConfig,
    vegasPresses,
    useGroups,
    groups,
    vegasConfig,
  ]);

  // --- 3. DATA FETCHING ---
  const fetchInitialData = async (currentUser) => {
    if (!currentUser?.id) return false;
    let fetchedActiveRound = null;
    try {
      const { data: allHistory } = await supabase
        .from("round_history")
        .select("*")
        .order("date", { ascending: false })
        .order("created_at", { ascending: false });

      if (allHistory) setRoundHistory(allHistory);

      const { data: profiles } = await supabase.from("profiles").select("*");

      if (profiles) {
        setPlayerDirectory(profiles);
        const hcpMap = {};
        profiles.forEach((p) => {
          const userRounds = allHistory
            ? allHistory.filter(
                (r) =>
                  (r.players && r.players.includes(p.id)) || r.user_id === p.id
              )
            : [];
          console.log(
            `Rounds for ${p.full_name}:`,
            userRounds.map((r) => ({
              date: r.date,
              total_score: r.total_score,
              total_par: r.total_par,
            }))
          );
          // REPLACE your hcpMap logic with this:
          // ... inside your loop
          const hcpResult = calculateCustomHandicap(
            userRounds,
            parseFloat(p.initial_handicap || 4.0),
            p.full_name,
            p.id
          );

          // Store the WHOLE object {current: 0, diff: -4, ...}
          hcpMap[p.id] = hcpResult.current;

          let numericValue =
            typeof hcpResult === "object" ? hcpResult.current : hcpResult;
          if (p.calculated_handicap !== numericValue) {
            supabase
              .from("profiles")
              .update({ calculated_handicap: numericValue })
              .eq("id", p.id)
              .then();
          }
        });

        console.log("FINAL HCP MAP FOR LEADERBOARD:", hcpMap);
        setPlayerHandicaps(hcpMap);
      }

      const { data: majorData } = await supabase
        .from("major_history")
        .select("*");
      if (majorData) setMajors(majorData);

      const { data: courses } = await supabase.from("courses").select("*");
      if (courses) setCourseDirectory(courses);

      // CHECK FOR ACTIVE ROUND
      const { data: fetchedActiveRoundData, error: activeRoundError } = await supabase
        .from("active_rounds")
        .select("*")
        .eq("user_id", currentUser.id)
        .maybeSingle();

      if (activeRoundError) {
        console.error("Failed to fetch active round:", activeRoundError);
      }
      fetchedActiveRound = fetchedActiveRoundData || null;

      const localPlayers = JSON.parse(localStorage.getItem("players") || "[]");
      const localScores = JSON.parse(localStorage.getItem("scores") || "{}");
      const hasLocalRoundData = localPlayers.length > 0 && Object.keys(localScores).length > 0;

      if (!fetchedActiveRound && hasLocalRoundData) {
        console.log("No active_rounds found in Supabase but localStorage has round data — attempting recovery");
        // Don't reset to dashboard; let the existing localStorage-driven state stand.
        // The bulk sync effect will re-push this data to active_rounds on next sync cycle.
      } else {
        setActiveRound(fetchedActiveRound || null);
      }

      const activeRound = fetchedActiveRound;
      // If no active round for this user, check for any active round to spectate
      if (!activeRound) {
        const { data: anyActiveRounds } = await supabase
          .from("active_rounds")
          .select("*")
          .order("updated_at", { ascending: false })
          .limit(1);

        const anyActiveRound = anyActiveRounds?.[0] || null;
        console.log(
          "anyActiveRound found:",
          anyActiveRound?.id,
          "spectatingRound will be set:",
          !!anyActiveRound
        );
        if (anyActiveRound) {
          setSpectatingRound(anyActiveRound);
          setView("leaderboard");
        }
      }
      if (activeRound) {
        // Full hydration from active_rounds — this is the authoritative recovery source
        setPlayers(activeRound.players || []);
        setScores(activeRound.scores || {});
        setSelectedCourse(activeRound.selected_course || null);
        setCurrentHoleIndex(activeRound.current_hole_index || 0);
        setSelectedGames(activeRound.selected_games || ["Stroke Play"]);
        if (activeRound.kept_holes) setKeptHoles(activeRound.kept_holes);
        if (activeRound.match_config) setMatchConfig(activeRound.match_config);
        if (activeRound.vegas_config) setVegasConfig(activeRound.vegas_config);
        if (activeRound.vegas_point_value) setVegasPointValue(activeRound.vegas_point_value);
        if (activeRound.vegas_presses) setVegasPresses(activeRound.vegas_presses);
        if (Array.isArray(activeRound.groups) && activeRound.groups.length > 0) {
          setGroups(activeRound.groups);
          setUseGroups(true);
        }
        if (activeRound.share_id) setShareId(activeRound.share_id);
        if (
          activeRound.player_handicaps &&
          Object.keys(activeRound.player_handicaps).length > 0
        ) {
          setPlayerHandicaps(activeRound.player_handicaps);
        }

        // Only redirect to scorecard if not already on a stats view
        const statsViews = [
          "holeStats",
          "holeStatsBreakdown",
          "stats",
          "leaderboard",
          "verticalScorecard",
        ];
        if (!statsViews.includes(localStorage.getItem("activeView"))) {
          setView("scorecard");
        }
        // --- FETCH HOLE STATS ONLY IF A ROUND IS ACTIVE ---
        const { data: activeStats } = await supabase
          .from("hole_stats")
          .select("*")
          .eq("user_id", currentUser.id)
          .is("round_id", null); // Fetch "unattached" stats

        if (activeStats && activeStats.length > 0) {
          const transformedStats = {};
          activeStats.forEach((s) => {
            if (!transformedStats[s.player_name])
              transformedStats[s.player_name] = {};
            transformedStats[s.player_name][s.hole_number] = {
              putts: s.putts,
              fairway: s.fairway_hit,
              gir: s.green_in_regulation,
            };
          });
          setAdvancedStats(transformedStats);
        }
      } else if (!hasLocalRoundData) {
        // No active round in Supabase AND no localStorage data — safe to clear
        setPlayers([]);
        setScores({});
        setSelectedCourse(null);
        setCurrentHoleIndex(0);
      }
      // if hasLocalRoundData but no Supabase round, leave state as-is (recovery mode)
    } catch (err) {
      console.error("Fetch failed:", err);
    }
    return !!fetchedActiveRound;
  };
  const memoizedRefresh = useCallback(() => {
    if (user) {
      console.log("🔄 Real-time refresh triggered...");
      fetchInitialData(user);
    }
  }, [user]);
  // --- 4. INITIALIZATION HOOKS ---
  useEffect(() => {
    const initApp = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const shareParam = params.get("share");
        const isLiveShare = params.get("view") === "live" && shareParam;

        // Handle live share link (?view=live&share=SHARE_ID) before any auth gate
        if (isLiveShare) {
          // Try active_rounds first (round still in progress)
          const { data: liveData } = await supabase
            .from("active_rounds")
            .select("*")
            .eq("share_id", shareParam)
            .maybeSingle();

          if (liveData) {
            const playerIds = liveData.players || [];
            const { data: profiles } = await supabase
              .from("profiles")
              .select("id, full_name")
              .in("id", playerIds);
            console.log("Public view player IDs:", playerIds);
            console.log("Public view profiles fetched:", profiles);
            console.log("playerDirectory set to:", profiles);
            if (profiles) setPlayerDirectory(profiles);

            const liveRound = {
              id: liveData.share_id,
              share_id: liveData.share_id,
              scores: liveData.scores,
              players: liveData.players,
              player_handicaps: liveData.player_handicaps,
              course_data: liveData.selected_course,
              course_name: liveData.selected_course?.name,
              selected_games: liveData.selected_games || ["Stroke Play"],
              date: new Date().toISOString().split("T")[0],
            };
            setSelectedSummaryRound(liveRound);
            setIsPublicView(true);
            setIsLiveShareView(true);
            setLiveShareId(shareParam);
            setView("leaderboard");
            setAppReady(true);
            return;
          }

          // Fall back to round_history (round already finalized)
          const { data: histData } = await supabase
            .from("round_history")
            .select("*")
            .eq("share_id", shareParam)
            .maybeSingle();

          if (histData) {
            const playerIds = histData.players || [];
            const { data: profiles } = await supabase
              .from("profiles")
              .select("id, full_name")
              .in("id", playerIds);
            console.log("Public view player IDs:", playerIds);
            console.log("Public view profiles fetched:", profiles);
            console.log("playerDirectory set to:", profiles);
            if (profiles) setPlayerDirectory(profiles);

            setSelectedSummaryRound(histData);
            setIsPublicView(true);
            setView("leaderboard");
            setAppReady(true);
            return;
          }
          // If neither found (RLS or bad ID), fall through to normal auth flow
        }

        const isPublicLeaderboard = params.get("view") === "leaderboard" && params.get("round");

        // Handle public leaderboard link FIRST, before any auth gate
        if (isPublicLeaderboard) {
          const roundId = params.get("round");
          const { data } = await supabase
            .from("round_history")
            .select("*")
            .eq("id", roundId)
            .single();
          if (data) {
            setSelectedSummaryRound(data);
            setIsPublicView(true);
            setView("leaderboard");
            // Still try to get session in background but don't block on it
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
              setUser(session.user);
              await fetchInitialData(session.user);
            }
            return;
          }
          // If round fetch failed (RLS blocks anon), fall through to normal auth flow
        }

        // Normal flow for non-public-link visits
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          const hadActiveRound = await fetchInitialData(session.user);

          // Fallback: if saved view is "scorecard" but no active round was found,
          // retry active_rounds fetch once more before giving up
          if (!hadActiveRound && localStorage.getItem("activeView") === "scorecard") {
            const { data: retryRound } = await supabase
              .from("active_rounds")
              .select("*")
              .eq("user_id", session.user.id)
              .maybeSingle();
            if (retryRound?.selected_course) {
              console.log("initApp fallback: recovered active round on retry");
              setSelectedCourse(retryRound.selected_course);
              setPlayers(retryRound.players || []);
              setScores(retryRound.scores || {});
              setCurrentHoleIndex(retryRound.current_hole_index || 0);
              setSelectedGames(retryRound.selected_games || ["Stroke Play"]);
              if (retryRound.match_config) setMatchConfig(retryRound.match_config);
              if (retryRound.vegas_config) setVegasConfig(retryRound.vegas_config);
              if (retryRound.player_handicaps) setPlayerHandicaps(retryRound.player_handicaps);
              setView("scorecard");
            } else {
              setView("dashboard");
            }
          }

          // Handle deep link params for authenticated users
          if (params.get("view") === "leaderboard") {
            const roundId = params.get("round");
            if (roundId) {
              const { data } = await supabase
                .from("round_history")
                .select("*")
                .eq("id", roundId)
                .single();
              if (data) {
                setSelectedSummaryRound(data);
              }
            }
            setView("leaderboard");
            if (window.navigator.standalone !== true) setShowDeepLinkBanner(true);
          } else if (params.get("view") === "diceRoller") {
            setView("diceRoller");
          } else if (!isRoundActive) {
            const savedView = localStorage.getItem("activeView");
            const safeViews = ["history", "roundSummary", "holeStatsBreakdown", "stats", "courseManager", "playerManager", "majors", "poty", "playersList", "quickRound", "birdieBet"];

            // Also reset leaderboard to dashboard if no round context
            if (savedView === "leaderboard" && !activeRound && !selectedSummaryRound) {
              setView("dashboard");
              localStorage.setItem("activeView", "dashboard");
              return;
            }

            if (safeViews.includes(savedView) && !activeRound) {
              setView("dashboard");
              localStorage.setItem("activeView", "dashboard");
            }
          }
        } else {
          setView("login");
        }
      } catch (err) {
        console.error("Init failed:", err);
      } finally {
        setAppReady(true);
      }
    };
    initApp();

    // *** DELETED THE EXTRA memoizedRefresh THAT WAS HERE ***

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("AUTH EVENT FIRED:", event, "at", new Date().toISOString());
      if (event === "SIGNED_IN" && session?.user) {
        setUser(session.user);
        fetchInitialData(session.user);
      } else if (event === "SIGNED_OUT") {
        // If this was triggered by handleLogout, skip — handleLogout manages its own state cleanup
        if (intentionalLogoutRef.current) {
          intentionalLogoutRef.current = false;
          return;
        }
        // Otherwise verify this is a real sign-out, not a false signal from a token refresh hiccup
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession?.user) {
          console.log("SIGNED_OUT event fired but session still valid — ignoring (likely a token refresh glitch)");
          return;
        }

        setUser(null);
        // Preserve round-in-progress data instead of nuking everything,
        // in case this fires unexpectedly while a round is active
        const hasActiveRoundData = JSON.parse(localStorage.getItem("players") || "[]").length > 0;
        if (hasActiveRoundData) {
          console.warn("Signing out with an active round in progress — preserving round data in localStorage");
        } else {
          localStorage.clear();
        }
        setView("login");
      }
    });

    // *** DELETED THE OTHER EXTRA memoizedRefresh THAT WAS HERE ***

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !appReady) {
        setAppReady(true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    // Check query params (password reset flow)
    const params = new URLSearchParams(window.location.search);
    if (
      params.get("type") === "recovery" ||
      params.get("view") === "updatePassword"
    ) {
      setView("updatePassword");
      return;
    }

    // Check URL hash (invite flow)
    const hash = window.location.hash;
    if (hash && hash.includes("type=invite")) {
      setView("updatePassword");
      return;
    }
    if (hash && hash.includes("type=recovery")) {
      setView("updatePassword");
      return;
    }
  }, []);

  // NEW: Fetch hole stats when HoleStatsBreakdown view opens
  useEffect(() => {
    if (view === "holeStatsBreakdown" && user) {
      console.log("Fetching hole stats for user.id:", user.id);
      supabase
        .from("hole_stats")
        .select("*")
        .eq("user_id", user.id)
        .then(({ data, error }) => {
          console.log("hole stats fetch result:", data?.length, error);
          if (data) setBreakdownHoleStats(data);
        });
    }
  }, [view, user]);
  // Spectator real-time subscription
  useEffect(() => {
    if (!spectatingRound) return;

    const channel = supabase
      .channel("spectate-active-round")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "active_rounds" },
        (payload) => {
          setSpectatingRound(payload.new);
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [spectatingRound?.user_id]);

  // Realtime updates for public live share viewers
  useEffect(() => {
    if (!isLiveShareView || !liveShareId) return;

    const channel = supabase
      .channel(`live-share-${liveShareId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "active_rounds",
          filter: `share_id=eq.${liveShareId}`,
        },
        (payload) => {
          const updated = payload.new;
          setSelectedSummaryRound({
            id: updated.share_id,
            share_id: updated.share_id,
            scores: updated.scores,
            players: updated.players,
            player_handicaps: updated.player_handicaps,
            course_data: updated.selected_course,
            course_name: updated.selected_course?.name,
            selected_games: updated.selected_games || ["Stroke Play"],
            date: new Date().toISOString().split("T")[0],
          });
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [isLiveShareView, liveShareId]);

  useEffect(() => {
    if (isRoundActive) return; // Don't watch if we're the ones playing

    const channel = supabase
      .channel("watch-for-new-rounds")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "active_rounds" },
        async (payload) => {
          console.log("Active round change detected:", payload);
          if (payload.new && payload.eventType !== "DELETE") {
            // A round was started or updated - fetch it
            const { data: activeRounds } = await supabase
              .from("active_rounds")
              .select("*")
              .order("updated_at", { ascending: false })
              .limit(1);

            const anyActiveRound = activeRounds?.[0] || null;
            if (anyActiveRound) {
              setSpectatingRound(anyActiveRound);
              setView("leaderboard");
            }
          } else if (payload.eventType === "DELETE") {
            // Round was finished
            setSpectatingRound(null);
            await fetchInitialData(user);
          }
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [isRoundActive, user]);

  // --- 5. APP LOGIC HANDLERS ---
  const syncActiveRound = async () => {
    if (!user || players.length === 0) return;
    const isScorekeeper = groups.some(g => g.scorekeeperId === user.id);
    const isRoundOwner = activeRound?.user_id === user.id;
    if (!isRoundOwner && !isScorekeeper) return;

    // 1. Sync main round data to active_rounds
    const activeData = {
      user_id: activeRound?.user_id || user.id,
      players,
      scores,
      selected_course: selectedCourse,
      player_handicaps: playerHandicaps,
      current_hole_index: currentHoleIndex,
      selected_games: selectedGames,
      kept_holes: keptHoles,
      vegas_config: vegasConfigRef.current,
      match_config: matchConfigRef.current,
      is_nassau: isNassau,
      groups: useGroups ? groups : null,
      vegas_presses: vegasPresses,
      share_id: shareId,
      updated_at: new Date().toISOString(),
    };

    const { error: roundError } = await supabase
      .from("active_rounds")
      .upsert(activeData);
    if (roundError)
      console.error("Active round sync failed:", roundError.message);

    // 2. Sync Advanced Stats to hole_stats
    if (Object.keys(advancedStats).length > 0) {
      const statsToUpload = [];
      Object.entries(advancedStats).forEach(([playerName, holes]) => {
        Object.entries(holes).forEach(([holeNum, stats]) => {
          const playerProfile = playerDirectory.find(
            (p) => p.full_name === playerName
          );
          statsToUpload.push({
            user_id: user.id,
            player_name: playerProfile?.id || playerName,
            hole_number: parseInt(holeNum, 10),
            putts: stats.putts || 0,
            fairway_hit: stats.fairway || stats.fairway_hit || null,
            green_in_regulation:
              stats.green || stats.green_in_regulation || stats.gir || null,
            score: scores[playerProfile?.id]?.[parseInt(holeNum)] || scores[user?.id]?.[parseInt(holeNum)] || null, // ✅ ADD
            par: selectedCourse?.holes?.[parseInt(holeNum) - 1]?.par || null, // ✅ ADD
            round_id: null, // This marks them as "Live/Active"
          });
        });
      });

      // We use upsert so it updates existing rows rather than creating 18 new rows every time
      const { error: statsError } = await supabase
        .from("hole_stats")
        .upsert(statsToUpload, {
          onConflict: "user_id,player_name,hole_number,round_id",
        });

      if (statsError)
        console.error("Hole stats sync failed:", statsError.message);
    }

    // 3. Auto-sync birdies to birdie_bet_entries when playing home course
    const homeCourseId = playerDirectory.find(p => p.home_course_id)?.home_course_id;
    if (homeCourseId && selectedCourse?.id === homeCourseId && players.length > 0) {
      const currentYear = new Date().getFullYear();
      const today = new Date().toISOString().split("T")[0];
      const birdiesToSync = [];

      players.forEach(player => {
        const playerScores = scores[player.id] || {};
        selectedCourse?.holes?.forEach((hole, index) => {
          const holeNum = index + 1;
          const score = Number(playerScores[holeNum]);
          if (score > 0 && score <= hole.par - 1) {
            birdiesToSync.push({
              player_id: player.id,
              hole_number: holeNum,
              date: today,
              year: currentYear,
            });
          }
        });
      });

      if (birdiesToSync.length > 0) {
        await supabase
          .from("birdie_bet_entries")
          .upsert(birdiesToSync, {
            onConflict: "player_id,hole_number,year",
            ignoreDuplicates: true,
          });
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      syncActiveRound();
    }, 1000);
    return () => clearTimeout(timer);
  }, [
    scores,
    players,
    currentHoleIndex,
    selectedCourse,
    advancedStats,
    matchConfig,
    vegasConfig,
    useGroups,
    groups,
    shareId,
  ]);

  useEffect(() => {
    if (!selectedGames.includes("Vegas")) return;
    if (vegasPointValue > 0) return;
    if (vegasValuePrompted) return;

    const hasFirstHole = players.length > 0 && Object.keys(scores).length > 0 &&
      players.some(p => {
        const pScores = scores[p.id] || {};
        return Object.keys(pScores).length > 0;
      });

    if (hasFirstHole) {
      setShowVegasValueModal(true);
      setVegasValuePrompted(true);
    }
  }, [scores, selectedGames, vegasPointValue, vegasValuePrompted, players]);

  const handleFinishRound = async () => {
    if (!user) return;

    // MISSING SCORE SAFETY CHECK
    const numHoles = selectedCourse?.holes?.length || 18;
    const missingByPlayer = {};

    for (let i = 1; i <= numHoles; i++) {
      const anyScoreOnHole = players.some((p) => scores[p.id]?.[i]);
      if (anyScoreOnHole) {
        players.forEach((p) => {
          if (!scores[p.id]?.[i]) {
            if (!missingByPlayer[p.id]) missingByPlayer[p.id] = [];
            missingByPlayer[p.id].push(i);
          }
        });
      }
    }

    const missingPlayers = Object.keys(missingByPlayer);
    if (missingPlayers.length > 0) {
      let message = "Wait! Some players are missing scores...\n\n";
      missingPlayers.forEach((id) => {
        const name = players.find((p) => p.id === id)?.full_name || id;
        message += `- ${name}: Hole(s) ${missingByPlayer[id].join(", ")}\n`;
      });
      message += "\nDo you want to proceed and finish the round anyway?";

      if (!window.confirm(message)) return;
    }

    // Calculate total score and par for each player for handicap use
    const totalPar =
      selectedCourse?.holes?.reduce((sum, h) => sum + (h.par || 0), 0) || 72;
    const playerTotals = {};
    players.forEach((p) => {
      const pScores = scores[p.id] || {};
      playerTotals[p.id] = Object.values(pScores).reduce(
        (sum, s) => sum + (Number(s) || 0),
        0
      );
    });

    const roundData = {
      user_id: user.id,
      date: roundDate,
      course_id: selectedCourse?.id,
      course_name: selectedCourse?.name,
      course_data: selectedCourse,
      players: players.map((p) => p.id), // store UUIDs only
      scores: scores, // already keyed by UUID
      player_handicaps: playerHandicaps, // already keyed by UUID
      selected_games: selectedGamesRef.current,
      match_config: matchConfigRef.current,
      vegas_config: vegasConfigRef.current,
      handicap_overrides: Object.keys(handicapOverridesRef.current || {}).length > 0
        ? Object.entries(handicapOverridesRef.current).reduce((acc, [id, overrideVal]) => {
            acc[id] = {
              override: overrideVal,
              calculated: playerHandicaps[id],
            };
            return acc;
          }, {})
        : {},
      kept_holes: keptHoles,
      is_major: activeMajorInfo.isMajor,
      major_name: activeMajorInfo.majorName,
      is_finalized: true,
      vegas_point_value: vegasPointValue,
      vegas_presses: vegasPresses,
      share_id: shareId,
    };
    try {
      // 1. Add .select() to get the newly inserted row back
      const { data: insertedRound, error } = await supabase
        .from("round_history")
        .insert([roundData])
        .select();

      if (error) throw error;

      // 2. Safely grab the ID from the returned data
      const newRoundId = insertedRound[0]?.id;

      // 3. Finalize advanced stats by linking them to the new round ID
      if (newRoundId && Object.keys(advancedStats).length > 0) {
        const { error: statsError } = await supabase
          .from("hole_stats")
          .update({ round_id: newRoundId }) // Give them the official ID
          .eq("user_id", user.id) // Only your stats
          .is("round_id", null); // Only the "live" ones from this round

        if (statsError) {
          console.error("Error finalizing advanced stats:", statsError.message);
        }
      }
      // Delete active round from DB
      await supabase.from("active_rounds").delete().eq("user_id", user.id);

      setSelectedSummaryRound(roundData);

      // Clear active state
      setScores({});
      setPlayers([]);
      setKeptHoles({});
      setCurrentHoleIndex(0);
      localStorage.setItem("currentHoleIndex", 0);
      setActiveMajorInfo({ isMajor: false, majorName: null });
      setMatchConfig({ mode: "1v1", teamA: [], teamB: [] });
      localStorage.removeItem("matchConfig");
      setVegasPresses({"front":{"teamA":null,"teamB":null},"back":{"teamA":null,"teamB":null}});
      localStorage.removeItem("vegasPresses");
      setSelectedGames(["Stroke Play"]);
      localStorage.setItem("selectedGames", JSON.stringify(["Stroke Play"]));
      setVegasValuePrompted(false);
      setShareId(null);
      // Check for 2s and set dice count
      const twosCount = players.reduce((total, player) => {
        const playerScores = scores[player.id] || {};
        const twos = Object.values(playerScores).filter(
          (s) => Number(s) === 2
        ).length;
        return total + twos;
      }, 0);
      if (twosCount > 0) {
        setDiceCount(twosCount);
      }

      // Refresh data & show summary
      await fetchInitialData(user);
      setView("leaderboard");
    } catch (err) {
      console.error("Error saving round:", err.message);
      alert("Failed to save round. Check console.");
    }
  };

  const cancelRound = async () => {
    if (window.confirm("Abandon this round?")) {
      // 1. Delete the active round from SUPABASE
      if (user) {
        try {
          await supabase.from("active_rounds").delete().eq("user_id", user.id);
        } catch (err) {
          console.error("Failed to clear cloud round:", err.message);
        }
      }

      // 2. Explicitly clear all round-related localStorage keys
      [
        "players", "scores", "selectedCourse", "currentHoleIndex",
        "selectedGames", "matchTeams", "isNassau", "keptHoles",
        "matchConfig", "vegasConfig", "activeView",
      ].forEach((key) => localStorage.removeItem(key));

      // 3. Reset React State
      setScores({});
      setPlayers([]);
      setMatchTeams(null);
      setCurrentHoleIndex(0);
      setSelectedCourse(null);
      setAdvancedStats({});
      setSelectedGames(["Stroke Play"]);
      setKeptHoles({});
      setVegasPresses({"front":{"teamA":null,"teamB":null},"back":{"teamA":null,"teamB":null}});
      localStorage.removeItem("vegasPresses");
      setVegasValuePrompted(false);
      setShareId(null);

      // 4. Navigate after all clearing is done
      setView("dashboard");
    }
  };

  const handleLogout = async () => {
    try {
      const hasActiveRoundData = players.length > 0;
      if (hasActiveRoundData) {
        const confirmLogout = window.confirm("You have an active round in progress. Logging out may lose your progress. Continue?");
        if (!confirmLogout) return;
      }

      // Signal that this is an intentional logout so the SIGNED_OUT handler doesn't fight it
      intentionalLogoutRef.current = true;
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUser(null);
      setPlayers([]);
      setScores({});
      setSelectedCourse(null);
      setRoundHistory([]);

      localStorage.clear();
      setView("login");
    } catch (err) {
      intentionalLogoutRef.current = false;
      console.error("Logout failed:", err.message);
      setView("login");
    }
  };

  const handleSaveCourse = async (courseData) => {
    const { error } = await supabase.from("courses").upsert(courseData);
    if (!error) {
      await fetchInitialData(user);
      setView("courseManager");
    }
  };

  const handleSetHomeCourse = async (courseId) => {
    console.log("handleSetHomeCourse called with:", courseId);
    try {
      const updates = playerDirectory.map(p =>
        supabase
          .from("profiles")
          .update({ home_course_id: courseId })
          .eq("id", p.id)
      );
      const results = await Promise.all(updates);
      const errors = results.filter(r => r.error);
      if (errors.length > 0) throw new Error(errors[0].error.message);
      alert("Home course updated for all players!");
      await fetchInitialData(user);
    } catch (err) {
      console.error("Error updating home course:", err.message);
      alert("Error: " + err.message);
    }
  };
  // Build guaranteed handicap map for leaderboard
  const leaderboardHandicaps = React.useMemo(() => {
    const result = {};
    playerDirectory.forEach((p) => {
      if (p.id) {
        result[p.id] = p.calculated_handicap ?? p.initial_handicap ?? 0;
      }
    });
    Object.entries(playerHandicaps).forEach(([id, hcp]) => {
      result[id] = hcp;
    });
    return result;
  }, [playerDirectory, playerHandicaps]);
  const handleStatsFinish = () => {
    // Advance to next hole if not on hole 18
    if (currentHoleIndex < (selectedCourse?.holes?.length || 18) - 1) {
      setCurrentHoleIndex((prev) => prev + 1);
    }
    setView("scorecard");
  };
  // --- RENDER ---
  if (!appReady) return null; // or a loading spinner

  const noNavViews = [
    "login",
    "signup",
    "updatePassword",
    "scorecard",
    "forgotPassword",
    "verticalScorecard",
    "leaderboard",
    "dashboard",
    "playersList",
    "quickRound",
  ];
  const needsTopPadding = user && !noNavViews.includes(view);

  return (
    <div
      className="App"
      style={{
        minHeight: "100vh",
        paddingBottom: "100px",
        paddingTop: needsTopPadding ? "55px" : "0px",
      }}
    >
      {showVegasValueModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.7)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "20px"
        }}>
          <div style={{
            background: "white", borderRadius: "20px", padding: "28px",
            width: "100%", maxWidth: "340px", textAlign: "center"
          }}>
            <div style={{ fontSize: "2rem", marginBottom: "8px" }}>💰</div>
            <h3 style={{ margin: "0 0 6px 0", color: "#1b4332", fontSize: "1.2rem" }}>Vegas Point Value</h3>
            <p style={{ color: "#64748b", fontSize: "0.85rem", marginBottom: "20px" }}>
              How much is each point worth?
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px", marginBottom: "20px" }}>
              {[0.25, 0.50, 0.75, 1.00, 1.25, 1.50, 1.75, 2.00, 2.50, 3.00, 4.00, 5.00].map(val => (
                <button
                  key={val}
                  onClick={() => {
                    setVegasPointValue(val);
                    localStorage.setItem("vegasPointValue", val);
                    setShowVegasValueModal(false);
                  }}
                  style={{
                    padding: "12px 4px",
                    borderRadius: "10px",
                    border: "2px solid #e2e8f0",
                    background: "white",
                    fontWeight: "800",
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    color: "#1b4332"
                  }}
                >
                  ${val.toFixed(2)}
                </button>
              ))}
            </div>

            <button
              onClick={() => setShowVegasValueModal(false)}
              style={{
                width: "100%", padding: "12px", background: "none",
                border: "none", color: "#94a3b8", fontSize: "0.85rem",
                cursor: "pointer", fontWeight: "600"
              }}
            >
              Skip for now
            </button>
          </div>
        </div>
      )}

      {showDeepLinkBanner && (
        <div
          style={{
            background: "#1b4332",
            color: "white",
            textAlign: "center",
            padding: "10px 16px",
            fontSize: "13px",
            position: "sticky",
            top: 0,
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          📲 Add to Home Screen for the best experience
          <button
            onClick={() => setShowDeepLinkBanner(false)}
            style={{
              background: "none",
              border: "none",
              color: "white",
              fontSize: "18px",
              cursor: "pointer",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            ✕
          </button>
        </div>
      )}
      {user && view !== "login" && !isPublicView && (
        <Navigation
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          isAdmin={isAdmin}
          navigateTo={setView}
          isInRound={isRoundActive}
          onCancel={cancelRound}
          onFinish={handleFinishRound}
          onLogout={handleLogout}
        />
      )}

      {view === "forgotPassword" && (
        <ForgotPassword
          onBack={() => setView("login")}
          onSuccess={() => setView("login")}
        />
      )}
      {view === "login" && (
        <Login
          onLoginSuccess={(u) => {
            setUser(u);
            setView("dashboard");
          }}
          onGoToSignup={() => setView("signup")}
          onForgotPassword={() => setView("forgotPassword")} // <-- ADD THIS LINE
        />
      )}

      {view === "signup" && <Signup onBack={() => setView("login")} />}

      {view === "dashboard" && (
        <Dashboard
          user={user}
          setView={(newView) => {
            if (newView === "stats") {
              // Find the profile that matches the logged-in user
              const currentUserProfile = playerDirectory.find(
                (p) => p.id === user?.id
              );
              // Use a safe fallback so it doesn't crash if profile isn't found
              setStatsTargetPlayer(
                currentUserProfile?.full_name ||
                  user?.user_metadata?.full_name ||
                  "Me"
              );
            }
            setView(newView);
          }}
          roundHistory={roundHistory}
          playerDirectory={playerDirectory}
          activeRound={isRoundActive}
          onResume={() => setView("scorecard")}
          playerHandicaps={playerHandicaps}
          userHandicap={(() => {
            const uProfile = playerDirectory.find((p) => p.id === user?.id || p.email === user?.email);
            return leaderboardHandicaps[uProfile?.id] ?? uProfile?.calculated_handicap ?? 0;
          })()}
        />
      )}
      {view === "setup" && (
        <Setup
          roundDate={roundDate}
          setRoundDate={setRoundDate}
          courses={courseDirectory}
          selectedCourse={selectedCourse}
          setSelectedCourse={setSelectedCourse}
          playerDirectory={playerDirectory}
          players={players}
          setPlayers={setPlayers}
          matchConfig={matchConfig}
          setMatchConfig={setMatchConfig}
          vegasConfig={vegasConfig}
          setVegasConfig={setVegasConfig}
          playerHandicaps={playerHandicaps}
          setPlayerHandicaps={setPlayerHandicaps}
          startingHole={startingHole}
          setStartingHole={setStartingHole}
          selectedGames={selectedGames}
          setSelectedGames={setSelectedGames}
          onBegin={async (config) => {
            if (config.matchConfig) setMatchConfig(config.matchConfig);
            if (config.vegasConfig) setVegasConfig(config.vegasConfig);
            if (config.handicapOverrides) {
              handicapOverridesRef.current = config.handicapOverrides;
              setPlayerHandicaps(prev => ({ ...prev, ...config.handicapOverrides }));
            }
            setIsNassau(config.isNassau);
            setSelectedGames(config.selectedGames);
            setActiveMajorInfo({
              isMajor: config.isMajor,
              majorName: config.majorName,
            });
            if (config.useGroups !== undefined) setUseGroups(config.useGroups);
            if (config.groups) setGroups(config.groups);

            // Immediately write full initial state to active_rounds before any score is entered.
            // players and selectedCourse are already committed to React state at this point.
            if (user && players.length > 0 && selectedCourse) {
              const newShareId = crypto.randomUUID();
              setShareId(newShareId);
              const initialHandicaps = { ...playerHandicaps, ...(config.handicapOverrides || {}) };
              await supabase.from("active_rounds").upsert({
                user_id: user.id,
                players,
                scores: {},
                selected_course: selectedCourse,
                player_handicaps: initialHandicaps,
                current_hole_index: 0,
                selected_games: config.selectedGames || ["Stroke Play"],
                match_config: config.matchConfig || { mode: "1v1", teamA: [], teamB: [] },
                vegas_config: config.vegasConfig || { teamA: [], teamB: [] },
                kept_holes: {},
                is_nassau: config.isNassau || false,
                groups: config.useGroups ? (config.groups || []) : null,
                vegas_presses: { front: { teamA: null, teamB: null }, back: { teamA: null, teamB: null } },
                vegas_point_value: 0,
                share_id: newShareId,
                updated_at: new Date().toISOString(),
              });
            }

            setView("scorecard");
          }}
          onBack={() => setView("dashboard")}
        />
      )}

      {view === "scorecard" && (() => {
        const myGroup = useGroups && groups.length > 0
          ? groups.find(g => g.scorekeeperId === user?.id || g.players.some(p => p.id === user?.id))
          : null;
        const scorecardPlayers = myGroup
          ? players.filter(p => myGroup.players.some(mp => mp.id === p.id))
          : players;
        return (
        <Scorecard
          selectedCourse={selectedCourse}
          players={scorecardPlayers}
          setPlayers={setPlayers}
          scores={scores}
          setScores={setScores}
          playerHandicaps={playerHandicaps}
          playerDirectory={playerDirectory}
          currentHoleIndex={currentHoleIndex}
          setCurrentHoleIndex={setCurrentHoleIndex}
          setView={setView}
          setStatsReturnView={setStatsReturnView} // ✅ ADD THIS
          onFinish={handleFinishRound}
          selectedGames={selectedGames}
          getHoleData={getHoleData}
          getNetScore={getNetScore}
          keptHoles={keptHoles}
          setKeptHoles={setKeptHoles}
          onNext={() =>
            setCurrentHoleIndex((prev) =>
              Math.min((selectedCourse?.holes?.length || 18) - 1, prev + 1)
            )
          }
          onPrev={() => setCurrentHoleIndex((prev) => Math.max(0, prev - 1))}
          onRecoverRound={() => fetchInitialData(user)}
        />
        );
      })()}

      {view === "leaderboard" &&
        (playerDirectory.length > 0 || isPublicView) &&
        (() => {
          const historicalSource =
            selectedSummaryRound ||
            (roundHistory.length > 0 ? roundHistory[0] : null);
          const isLiveSpectating = !isRoundActive && !!spectatingRound;
          const source = isRoundActive
            ? null
            : isLiveSpectating
            ? spectatingRound
            : historicalSource;
          console.log(
            "leaderboardHandicaps at render:",
            JSON.stringify(leaderboardHandicaps)
          );
          console.log("playerDirectory at render:", playerDirectory.length);
          console.log(
            "playerHandicaps at render:",
            JSON.stringify(playerHandicaps)
          );
          console.log("App keptHoles state:", JSON.stringify(keptHoles));
          console.log("App vegasConfig state:", JSON.stringify(vegasConfig));
          return (
            <Leaderboard
              players={
                isRoundActive
                  ? players
                  : isLiveSpectating
                  ? spectatingRound?.players || []
                  : source?.players || []
              }
              scores={
                isRoundActive
                  ? scores
                  : isLiveSpectating
                  ? spectatingRound?.scores || {}
                  : source?.scores || {}
              }
              selectedCourse={
                isRoundActive
                  ? selectedCourse
                  : isLiveSpectating
                  ? spectatingRound?.selected_course || null
                  : source?.course_data || null
              }
              playerHandicaps={
                isRoundActive
                  ? leaderboardHandicaps
                  : isLiveSpectating
                  ? leaderboardHandicaps
                  : source?.player_handicaps || leaderboardHandicaps
              }
              keptHoles={
                isRoundActive
                  ? keptHoles
                  : isLiveSpectating
                  ? spectatingRound?.kept_holes || {}
                  : source?.kept_holes || {}
              }
              selectedGames={
                isRoundActive
                  ? selectedGames
                  : isLiveSpectating
                  ? spectatingRound?.selected_games || ["Stroke Play"]
                  : source?.selected_games || ["Stroke Play"]
              }
              matchConfig={
                isRoundActive
                  ? matchConfig
                  : isLiveSpectating
                  ? spectatingRound?.match_config || null
                  : source?.match_config || null
              }
              vegasConfig={
                isRoundActive
                  ? vegasConfig
                  : isLiveSpectating
                  ? spectatingRound?.vegas_config || null
                  : source?.vegas_config || null
              }
              vegasPointValue={
                isRoundActive
                  ? vegasPointValue
                  : source?.vegas_point_value || 0
              }
              vegasPresses={
                isRoundActive
                  ? vegasPresses
                  : source?.vegas_presses || {"front":{"teamA":null,"teamB":null},"back":{"teamA":null,"teamB":null}}
              }
              onPress={(nine, team, holeNum) => {
                setVegasPresses(prev => ({
                  ...prev,
                  [nine]: { ...prev[nine], [team]: holeNum }
                }));
              }}
              roundDate={isRoundActive ? roundDate : source?.date}
              isHistorical={!isRoundActive && !isLiveSpectating}
              isMajor={
                isRoundActive ? activeMajorInfo.isMajor : source?.is_major
              }
              majorName={
                isRoundActive ? activeMajorInfo.majorName : source?.major_name
              }
              isFinalized={source?.is_finalized}
              setView={setView}
              playerDirectory={playerDirectory}
              onFinish={handleFinishRound}
              roundId={source?.id || null}
              onBack={() => {
                if (selectedSummaryRound) {
                  setView("roundSummary");
                } else {
                  setView(isRoundActive ? "scorecard" : "dashboard");
                }
              }}
              onSelectPlayer={(playerName) => {
                setTargetPlayer(playerName);
                setView("verticalScorecard");
              }}
              onTrackStats={() => {
                setStatsReturnView("leaderboard");
                setView("holeStats");
              }}
              roundHistory={roundHistory}
              onSelectRound={(round) => {
                setSelectedSummaryRound(round);
                setView("leaderboard");
              }}
              roundHandicapOverrides={
                isRoundActive
                  ? handicapOverridesRef.current
                  : source?.handicap_overrides || {}
              }
              isPublicView={isPublicView}
              shareId={shareId}
            />
          );
        })()}

      {view === "poty" && (
        <POTYView
          standings={potyStandings}
          onBack={() => setView("dashboard")}
        />
      )}

      {view === "playersList" && (
        <PlayersList
          players={playerDirectory}
          roundHistory={roundHistory}
          potyStandings={potyStandings}
          onSelectPlayer={(player) => {
            setStatsTargetPlayer(player.full_name || player); // ← just the name string
            setView("stats");
          }}
        />
      )}

      {view === "stats" && (
        <Stats
          user={user}
          homeCourseId={user?.home_course_id}
          players={playerDirectory}
          playerDirectory={playerDirectory}
          advancedStats={advancedStats}
          holeStats={Object.entries(advancedStats).flatMap(
            ([playerName, holes]) => {
              const playerProfile = playerDirectory.find(
                (p) => p.email === playerName
              );
              const playerFullName = playerProfile?.full_name || playerName;
              return Object.entries(holes).map(([holeNum, stat]) => ({
                player_name: playerName,
                hole_number: parseInt(holeNum),
                putts: stat.putts || 0,
                fairway_hit: stat.fairway || stat.fairway_hit || null,
                green_in_regulation:
                  stat.green || stat.green_in_regulation || stat.gir || null,
                is_live: true,
                score: scores?.[playerFullName]?.[parseInt(holeNum)] || null,
                par:
                  selectedCourse?.holes?.[parseInt(holeNum) - 1]?.par || null,
              }));
            }
          )}
          roundHistory={[
            ...(isRoundActive
              ? [
                  {
                    id: "live",
                    date: roundDate,
                    course_id: selectedCourse?.id,
                    course_name: selectedCourse?.name,
                    is_home_course: true,
                    scores: scores,
                    player_handicaps: playerHandicaps,
                    players: players,
                  },
                ]
              : []),
            ...roundHistory,
          ]}
          courses={courseDirectory}
          courseDirectory={courseDirectory}
          statsTargetPlayer={statsTargetPlayer}
          supabase={supabase}
          onRefresh={memoizedRefresh}
          navigateTo={(destination) => {
            console.log("navigateTo statsTargetPlayer:", statsTargetPlayer);
            setHoleStatsSource("stats");
            setView(destination);
          }}
          onBack={() => {
            setView(selectedSummaryRound ? "roundSummary" : "dashboard");
            setStatsTargetPlayer(null);
          }}
        />
      )}
      {view === "playerManager" && (
        <PlayerManager
          players={playerDirectory}
          onClose={() => setView("dashboard")}
          onUpdate={() => fetchInitialData(user)}
        />
      )}

      {view === "majors" && (
        <MajorsView history={majors} onBack={() => setView("dashboard")} />
      )}

      {user && !isPublicView && (
        <BottomNav
          currentView={view}
          setView={setView}
          isRoundActive={isRoundActive}
        />
      )}

      {view === "verticalScorecard" && (
        <VerticalScorecard
          players={
            targetPlayer
              ? [
                  playerDirectory.find((p) => p.full_name === targetPlayer) || {
                    id: targetPlayer,
                    full_name: targetPlayer,
                  },
                ]
              : players
          }
          selectedCourse={
            selectedCourse ||
            selectedSummaryRound?.course_data ||
            spectatingRound?.selected_course ||
            (roundHistory.length > 0 ? roundHistory[0]?.course_data : null)
          }
          scores={
            isRoundActive
              ? scores
              : selectedSummaryRound?.scores ||
                spectatingRound?.scores ||
                (roundHistory.length > 0 ? roundHistory[0]?.scores : {})
          }
          playerHandicaps={
            isRoundActive
              ? playerHandicaps
              : selectedSummaryRound?.player_handicaps ||
                spectatingRound?.player_handicaps ||
                (roundHistory.length > 0 ? roundHistory[0]?.player_handicaps : {})
          }
          onBack={() => {
            setTargetPlayer(null);
            if (isRoundActive) {
              setView("scorecard");
            } else if (selectedSummaryRound) {
              setView("roundSummary");
            } else {
              setView("leaderboard");
            }
          }}
          onViewStats={
            targetPlayer
              ? () => {
                  setScorecardStatsPlayer(targetPlayer);
                  setHoleStatsSource("scorecard");
                  setView("holeStatsBreakdown");
                }
              : null
          }
        />
      )}

      {view === "courseManager" && (
        <CourseManager
          courses={courseDirectory}
          homeCourseId={playerDirectory.find(p => p.home_course_id)?.home_course_id || null}
          onAdd={() => {
            setEditingCourse(null);
            setView("courseCreator");
          }}
          onEdit={(course) => {
            setEditingCourse(course);
            setView("courseCreator");
          }}
          setHomeCourse={handleSetHomeCourse}
        />
      )}

      {view === "courseCreator" && (
        <CourseCreator
          course={editingCourse}
          onSave={handleSaveCourse}
          onCancel={() => setView("courseManager")}
        />
      )}

      {view === "history" && (
        <RoundHistory
          roundHistory={roundHistory}
          setSelectedSummaryRound={setSelectedSummaryRound}
          setView={setView}
        />
      )}

      {view === "roundSummary" && (
        <RoundSummary
          user={user}
          roundData={selectedSummaryRound}
          setView={setView}
          setStatsTargetPlayer={setStatsTargetPlayer}
          playerDirectory={playerDirectory}
          setSelectedSummaryRound={setSelectedSummaryRound}
          isAdmin={isAdmin}
          onSaveScores={async (editedScores) => {
            if (!selectedSummaryRound?.id) return;

            const { error } = await supabase
              .from("round_history")
              .update({ scores: editedScores })
              .eq("id", selectedSummaryRound.id);

            if (error) {
              alert("Error saving scores: " + error.message);
              return;
            }

            setSelectedSummaryRound((prev) => ({ ...prev, scores: editedScores }));
            await fetchInitialData(user);
            alert("Scores updated! Handicaps recalculated.");
          }}
          supabase={supabase}
          onBack={() => {
            const hasHistory = roundHistory.some(
              (r) => r.id === selectedSummaryRound?.id
            );
            setSelectedSummaryRound(null);
            setView(hasHistory ? "history" : "dashboard");
          }}
        />
      )}
      {view === "holeStats" && (
        <HoleStats
          user={user}
          isRoundActive={isRoundActive} // Corrected name from activeRound
          setView={setView}
          advancedStats={advancedStats}
          setAdvancedStats={setAdvancedStats}
          returnView={statsReturnView}
          onFinish={handleStatsFinish}
          scores={scores}
          players={players}
          selectedCourse={selectedCourse}
        />
      )}
      {view === "holeStatsBreakdown" && (
        <HoleStatsBreakdown
          holeStats={breakdownHoleStats}
          authId={user?.id}
          activeName={(() => {
            if (scorecardStatsPlayer) return user?.id;
            const profile = playerDirectory.find(p => p.full_name === statsTargetPlayer);
            return profile?.id || user?.id;
          })()}
          playerKey={(() => {
            if (scorecardStatsPlayer) return user?.id;
            const profile = playerDirectory.find(p => p.full_name === statsTargetPlayer);
            return profile?.id || user?.id;
          })()}
          scores={scores}
          roundHistory={roundHistory}
          defaultMode={holeStatsSource === "scorecard" ? "live" : "hist"}
          onBack={() => {
            if (scorecardStatsPlayer) {
              setScorecardStatsPlayer(null);
              setHoleStatsSource("stats");
              setView("verticalScorecard");
            } else {
              setHoleStatsSource("stats");
              setView("stats");
            }
          }}
          courses={courseDirectory}
          derivedHomeId={user?.home_course_id}
        />
      )}
      {view === "birdieBet" && (
        <BirdieBetView
          playerDirectory={playerDirectory}
          courseDirectory={courseDirectory}
          user={user}
          userProfileId={
            playerDirectory.find((p) => p.email === user?.email)?.id
          }
        />
      )}
      {view === "diceRoller" && (
        <DiceRoller numDice={diceCount} onBack={() => setView("dashboard")} />
      )}
      {view === "quickRound" && (
        <QuickRound
          user={user}
          courses={courseDirectory}
          playerDirectory={playerDirectory}
          roundHistory={roundHistory}
          onBack={() => setView("dashboard")}
          onSave={async (roundData) => {
            const { error } = await supabase.from("round_history").insert([roundData]);
            if (!error) {
              const homeCourseId = playerDirectory.find(p => p.home_course_id)?.home_course_id;
              if (homeCourseId && roundData.course_id === homeCourseId && roundData.scores) {
                const currentYear = new Date().getFullYear();
                const birdiesToInsert = [];

                // Only works for hole-by-hole entries, not total score entries
                Object.entries(roundData.scores).forEach(([playerId, playerScores]) => {
                  if (playerScores.total !== undefined) return;
                  const course = courseDirectory.find(c => c.id === roundData.course_id);
                  if (!course?.holes) return;
                  course.holes.forEach((hole, index) => {
                    const holeNum = index + 1;
                    const score = Number(playerScores[holeNum]);
                    if (score > 0 && score <= hole.par - 1) {
                      birdiesToInsert.push({
                        player_id: playerId,
                        hole_number: holeNum,
                        date: roundData.date,
                        year: currentYear,
                      });
                    }
                  });
                });

                if (birdiesToInsert.length > 0) {
                  await supabase
                    .from("birdie_bet_entries")
                    .upsert(birdiesToInsert, {
                      onConflict: "player_id,hole_number,year",
                      ignoreDuplicates: true
                    });
                }
              }

              await fetchInitialData(user);
              setView("dashboard");
            } else {
              alert("Error saving round: " + error.message);
            }
          }}
        />
      )}
    </div>
  );
}
